import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAccount, useContractRead } from 'wagmi';
import { ToastContainer, toast } from 'react-toastify';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import 'react-toastify/dist/ReactToastify.css';
import { ethers } from 'ethers';

import ThemeToggle from './components/ThemeToggle';
import Footer from './components/Footer';
import MemoryChainABI from './MemoryChainABI.json';
import MemoryTokenABI from './MemoryTokenABI.json';
import PreSaleABI from './PreSaleABI.json';

// Lazy-loaded components
const Home = lazy(() => import('./components/Home'));
const About = lazy(() => import('./components/About'));
const MyProfile = lazy(() => import('./components/MyProfile'));
const SwapPage = lazy(() => import('./components/SwapPage'));
const Roadmap = lazy(() => import('./components/Roadmap'));

// Navigation Component
const Navigation = () => (
  <nav className="flex space-x-6">
    {[
      { path: '/', label: 'Home' },
      { path: '/about', label: 'About' },
      { path: '/roadmap', label: 'Roadmap & Tokenomics' },
      { path: '/profile', label: 'My Profile' },
      { path: '/swap', label: 'Swap' },
    ].map(({ path, label }) => (
      <motion.div key={path} whileHover={{ scale: 1.1, y: -2 }} transition={{ duration: 0.2 }}>
        <Link
          to={path}
          className={`text-lg font-medium ${
            window.location.pathname === path
              ? 'text-yellow-300 shadow-[0_0_10px_rgba(255,215,0,0.7)]'
              : 'text-white dark:text-white hover:text-yellow-300 dark:hover:text-yellow-300 transition-all duration-300 hover:shadow-[0_0_10px_rgba(255,215,0,0.5)]'
          }`}
        >
          {label}
        </Link>
      </motion.div>
    ))}
  </nav>
);

// Header Component
const Header = ({ handleRefresh }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className="w-full bg-gradient-to-r from-indigo-700 to-purple-700 dark:from-indigo-900 dark:to-purple-900 text-white shadow-2xl rounded-xl p-2 sm:p-4 flex justify-between items-center sticky top-0 z-20"
    >
      <div className="flex items-center space-x-2 sm:space-x-4">
        <motion.div
          whileHover={{ scale: 1.2, rotate: 360 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
          className="w-12 h-12 sm:w-16 sm:h-16"
        >
          <img
            src="/logo.png"
            alt="Memory Chain Logo"
            className="w-full h-full object-contain"
          />
        </motion.div>
        <div className="hidden sm:flex items-center space-x-6">
          <Navigation />
        </div>
      </div>
      <div className="flex items-center space-x-2 sm:space-x-2">
        <ConnectButton
          label="Connect Wallet"
          accountStatus="address"
          chainStatus="icon"
          showBalance={false}
          className="px-4 py-2 bg-white text-indigo-700 dark:bg-gray-800 dark:text-white rounded-full font-semibold shadow-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
        />
        <motion.button
          whileHover={{ scale: 1.1, boxShadow: '0 0 12px rgba(255, 255, 255, 0.7)' }}
          whileTap={{ scale: 0.95 }}
          onClick={handleRefresh}
          className="px-2 py-1 sm:px-4 sm:py-2 bg-white text-indigo-700 dark:bg-gray-800 dark:text-white rounded-full font-semibold shadow-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 text-xs sm:text-base"
        >
          Refresh
        </motion.button>
        <ThemeToggle />
        <button
          className="sm:hidden text-white focus:outline-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <svg className="w-5 h-5 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
          </svg>
        </button>
      </div>
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="sm:hidden absolute top-full left-0 right-0 bg-indigo-700 dark:bg-indigo-900 p-3 rounded-xl shadow-lg z-30 w-full mt-1"
        >
          <nav className="flex flex-col space-y-2">
            {[
              { path: '/', label: 'Home' },
              { path: '/about', label: 'About' },
              { path: '/roadmap', label: 'Roadmap & Tokenomics' },
              { path: '/profile', label: 'My Profile' },
              { path: '/swap', label: 'Swap' },
            ].map(({ path, label }) => (
              <motion.div key={path} whileHover={{ scale: 1.05, y: -1 }} transition={{ duration: 0.2 }}>
                <Link
                  to={path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block text-sm font-medium ${
                    window.location.pathname === path
                      ? 'text-yellow-300 shadow-[0_0_8px_rgba(255,215,0,0.7)]'
                      : 'text-white dark:text-white hover:text-yellow-300 dark:hover:text-yellow-300 transition-all duration-200 hover:shadow-[0_0_8px_rgba(255,215,0,0.5)]'
                  }`}
                >
                  {label}
                </Link>
              </motion.div>
            ))}
          </nav>
        </motion.div>
      )}
    </motion.header>
  );
};

// App Component
const App = () => {
  const { address, isConnected } = useAccount();
  const [contract, setContract] = useState(null);
  const [memToken, setMemToken] = useState(null);
  const [presaleContract, setPresaleContract] = useState(null);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [userMemories, setUserMemories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const contractAddress = '0x11CB4BbF026c337F7a59bF7DD7f49F3E04821602';
  const memTokenAddress = '0x87EFf129490666Afcb2D059d94B9001FeddFA98e';
  const presaleContractAddress = '0xB863773F5e31bc1e5400589287d5AD79c86d3469';

  // Initialize contracts
  useEffect(() => {
    const initializeContracts = async () => {
      try {
        // استفاده از provider پیش‌فرض مرورگر (مانند MetaMask)
        const provider = new ethers.BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();

        // تنظیم قراردادها
        const memoryChainContract = new ethers.Contract(contractAddress, MemoryChainABI, signer);
        const memoryTokenContract = new ethers.Contract(memTokenAddress, MemoryTokenABI, signer);
        const presaleContract = new ethers.Contract(presaleContractAddress, PreSaleABI, signer);

        setContract(memoryChainContract);
        setMemToken(memoryTokenContract);
        setPresaleContract(presaleContract);

        if (process.env.NODE_ENV === 'development') {
          console.log('Contracts initialized:', {
            memoryChainContract: contractAddress,
            memoryTokenContract: memTokenAddress,
            presaleContract: presaleContractAddress,
          });
        }
      } catch (err) {
        console.error('Error initializing contracts:', JSON.stringify(err, null, 2));
        setError('Failed to initialize contracts: ' + (err.message || 'Unknown error'));
        toast.error('Failed to initialize contracts. Please check your wallet connection.');
      }
    };

    if (window.ethereum && isConnected) {
      initializeContracts();
    }
  }, [isConnected]);

  // Fetch user memories
  const { data: memoriesData } = useContractRead({
    address: contractAddress,
    abi: MemoryChainABI,
    functionName: 'getUserTokens',
    args: [address],
    enabled: !!address,
  });

  useEffect(() => {
    if (memoriesData) {
      const formattedMemories = memoriesData.map((memory, index) => ({
        id: Number(memory.id || index + 1), // اطمینان از اینکه id یک عدد است
        text: memory.text || 'Unknown',
        category: memory.category || 'Uncategorized',
        votes: Number(memory.votes || 0),
      }));
      setUserMemories(formattedMemories);
      if (process.env.NODE_ENV === 'development') {
        console.log('User memories updated:', formattedMemories);
      }
    } else {
      setUserMemories([]);
    }
  }, [memoriesData]);

  // Memoize handleRefresh
  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
    if (process.env.NODE_ENV === 'development') {
      console.log('handleRefresh called, new refreshKey:', refreshKey + 1);
    }
  }, []); // وابستگی خالی، زیرا به state یا props خاصی وابسته نیست

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-200 dark:from-gray-900 dark:to-gray-800 w-full flex flex-col items-stretch p-0 sm:p-2 overflow-x-hidden font-sans">
        <Header handleRefresh={handleRefresh} />
        {isLoading ? (
          <div className="text-center mt-6 min-h-[400px] flex items-center justify-center w-full">
            <p className="text-xl font-medium text-gray-700 dark:text-gray-300">Loading...</p>
          </div>
        ) : error ? (
          <div className="text-center mt-6 min-h-[400px] flex items-center justify-center w-full">
            <p className="text-red-500 dark:text-red-400 text-xl font-medium">{error}</p>
          </div>
        ) : (
          <div className="w-full mt-6 flex-1 min-h-[calc(100vh-200px)]">
            <Suspense fallback={<div className="text-center mt-6 min-h-[400px] flex items-center justify-center w-full">Loading...</div>}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <Home
                      contract={contract}
                      account={address}
                      onRefresh={handleRefresh}
                      key={`home-${refreshKey}`}
                      userMemories={userMemories}
                      memToken={memToken}
                    />
                  }
                />
                <Route path="/about" element={<About />} />
                <Route
                  path="/profile"
                  element={
                    <MyProfile
                      contract={contract}
                      account={address}
                      userMemories={userMemories}
                      memToken={memToken}
                      onRefresh={handleRefresh}
                    />
                  }
                />
                <Route
                  path="/swap"
                  element={
                    <SwapPage
                      onRefresh={handleRefresh}
                      refreshTrigger={refreshKey}
                    />
                  }
                />
                <Route path="/roadmap" element={<Roadmap />} />
              </Routes>
            </Suspense>
          </div>
        )}
        <Footer />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
        />
      </div>
    </Router>
  );
};

export default App;